import FixedInput from "@/components/FixedInput";
import Skeleton from "@/components/Skeleton/Skeleton";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import useTranslation from "@/hooks/useTranslation";
import { CheckOutlined, DeleteOutlined, LoadingOutlined } from "@ant-design/icons";
import { Button, Flex, Input, message } from "antd";
import _ from "lodash";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import DeleteGoodsAndServicesItemAction from "../DeleteGoodsAndServicesItemAction";
import {
  GoodsAndServicesItem,
  useGetGoodsAndServicesItem,
  usePatchGoodsAndServicesItem,
} from "../useGoodsAndServices";

export default function GoodsAndServicesItemDetails({
  goodsAndServicesItemId,
}: {
  goodsAndServicesItemId: number;
}) {
  const { data: goodsAndServicesItem } = useGetGoodsAndServicesItem({ goodsAndServicesItemId });
  const patch = usePatchGoodsAndServicesItem();
  const smartQueryClient = useSmartQueryClient();
  const t = useTranslation();
  const navigate = useNavigate();

  const saveName = useCallback((name: string) => {
    patch
      .mutateAsync({ goodsAndServicesItemId, patch: { name } })
      .catch(() => message.error(t.errors.general));
  }, []);

  const debouncedSaveName = useMemo(() => _.debounce(saveName, 500), [saveName]);

  const updateName = (name: string) => {
    smartQueryClient.setQueryData<GoodsAndServicesItem>(
      ["goods_and_services", "detail", goodsAndServicesItemId, "details"],
      (goodsAndServicesItem) => {
        return goodsAndServicesItem
          ? {
              ...goodsAndServicesItem,
              name,
            }
          : undefined;
      }
    );

    debouncedSaveName(name);
  };

  return (
    <Flex
      vertical
      gap={24}
    >
      <Flex
        vertical
        gap={8}
      >
        ID
        <Input
          disabled
          value={goodsAndServicesItem?.id}
        />
      </Flex>
      <Flex
        vertical
        gap={8}
      >
        Name
        <FixedInput
          value={goodsAndServicesItem?.name}
          onChange={(name) => updateName(name)}
        />
      </Flex>
      <Flex
        justify="space-between"
        align="center"
      >
        {goodsAndServicesItem ? (
          <DeleteGoodsAndServicesItemAction
            goodsAndServicesItem={goodsAndServicesItem}
            onSucces={() => navigate("/goods_and_services")}
          >
            <Button
              danger
              disabled={!goodsAndServicesItem.deletable}
            >
              <DeleteOutlined /> Delete
            </Button>
          </DeleteGoodsAndServicesItemAction>
        ) : (
          <Skeleton style={{ width: "24px", height: "24px" }} />
        )}
        {patch.isLoading ? (
          <span>
            <LoadingOutlined /> Saving...
          </span>
        ) : (
          <span>
            <CheckOutlined /> Saved
          </span>
        )}
      </Flex>
    </Flex>
  );
}
